import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useApiErrorHandler } from "../lib/session";
import type { VisitCard as VisitCardDef, VisitState } from "../lib/types";
import { Notice } from "../components/bits";

/**
 * The pinned Besuch card.
 *
 * It is not dealt, cannot be thrown away, and awards nothing. It answers the one
 * question the points cannot: whether you were really here, rather than scoring a
 * place you passed through. Confirming it is on the honour system like everything
 * else -- the criteria are stated and you decide, so there is one button and no
 * checkboxes gating it.
 */
export default function VisitCardScreen({
  visit,
  setVisit,
  card,
}: {
  visit: VisitState | null;
  setVisit: (visit: VisitState) => void;
  card: VisitCardDef | null;
}) {
  const navigate = useNavigate();
  const handleError = useApiErrorHandler();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!visit || !card) return null;

  const place = visit.place;

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const { place: updated } = await api.confirmVisit(visit!.place.id);
      setVisit({ ...visit!, place: updated });
      navigate("/", { replace: true });
    } catch (err) {
      setError(handleError(err));
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <button className="btn btn--ghost" onClick={() => navigate(-1)} style={{ marginLeft: -12 }}>
        ← Zurück
      </button>

      <div className="cat-VISIT" style={{ marginTop: 8 }}>
        <div className="row" style={{ gap: 10, marginBottom: 12 }}>
          <span className="card__category">BESUCH · {place.name.toUpperCase()}</span>
          <span className="tiny muted" style={{ marginLeft: "auto" }}>
            Keine Punkte
          </span>
        </div>

        <p
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.6rem",
            lineHeight: 1.34,
            margin: "0 0 6px",
            fontWeight: 500,
          }}
        >
          {card.title}
        </p>
        <p className="tiny muted">{card.intro}</p>
      </div>

      <hr style={{ border: 0, borderTop: "1px solid var(--rule)", margin: "22px 0" }} />

      <ul className="criteria">
        {card.criteria.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <div className="stack" style={{ marginTop: 24 }}>
        {error ? <Notice tone="error">{error}</Notice> : null}

        {place.visited ? (
          <Notice>
            {place.name} ist schon als besucht eingetragen
            {place.visited_at
              ? ` — am ${new Date(place.visited_at).toLocaleDateString("de-DE")}`
              : ""}
            .
          </Notice>
        ) : (
          <button className="btn btn--lg btn--block" onClick={() => void confirm()} disabled={busy}>
            {busy ? <span className="spin" aria-hidden /> : null}
            {busy ? "Wird eingetragen" : card.confirm}
          </button>
        )}

        <p className="tiny muted" style={{ textAlign: "center", margin: 0 }}>
          {card.footnote} Es bringt keine Punkte und ändert das Ziel nicht — es hält nur fest,
          dass du wirklich hier warst.
        </p>
      </div>
    </div>
  );
}
